/**
 * Whitelist chips + a debounced member/role search picker. Non-technical
 * users never touch raw ids; pasting a snowflake still works via the
 * "add id directly" affordance.
 */
import { useEffect, useRef, useState } from "react";
import { AtSign, Plus, User, X } from "lucide-react";
import type { DiscordLinkDto, DiscordWhitelistEntryDto } from "@hydra-notes/shared";
import { useDiscord } from "./useDiscordStore";
import s from "./Discord.module.css";

const SNOWFLAKE_RE = /^\d{17,20}$/;

export function WhitelistManager({ link }: { link: DiscordLinkDto }) {
  const [entries, setEntries] = useState<DiscordWhitelistEntryDto[] | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<{
    members: { id: string; name: string; avatarUrl: string | null }[];
    roles: { id: string; name: string; color: string | null }[];
  } | null>(null);
  const [searching, setSearching] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reload = () => {
    void useDiscord
      .getState()
      .fetchWhitelist(link.id)
      .then((e) => setEntries(e ?? []));
  };
  useEffect(reload, [link.id]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!query.trim() || SNOWFLAKE_RE.test(query.trim())) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounce.current = setTimeout(() => {
      void useDiscord
        .getState()
        .search(link.guildId, query.trim())
        .then((r) => {
          setResults(r);
          setSearching(false);
        });
    }, 300);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query, link.guildId]);

  const add = async (kind: "user" | "role", discordId: string) => {
    setQuery("");
    setResults(null);
    setSearching(false);
    await useDiscord.getState().addWhitelist(link.id, kind, discordId);
    reload();
  };

  const remove = async (kind: string, discordId: string) => {
    await useDiscord.getState().removeWhitelist(link.id, kind, discordId);
    reload();
  };

  return (
    <div className={s.whitelist}>
      {entries === null ? (
        <p className={s.hint}>Loading whitelist...</p>
      ) : entries.length === 0 ? (
        <p className={s.hint}>No one whitelisted yet: the board answers to you alone.</p>
      ) : (
        <div className={s.chipRow}>
          {entries.map((e) => (
            <span key={`${e.kind}:${e.discordId}`} className={s.wlChip}>
              {e.kind === "user" ? (
                e.avatarUrl ? (
                  <img src={e.avatarUrl} alt="" className={s.wlAvatar} />
                ) : (
                  <User size={12} />
                )
              ) : (
                <span className={s.roleDot} style={e.color ? { background: e.color } : undefined} />
              )}
              {e.name}
              <span className={s.wlKind}>{e.kind}</span>
              <button
                type="button"
                className={s.wlRemove}
                aria-label={`Remove ${e.name}`}
                onClick={() => void remove(e.kind, e.discordId)}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={s.searchWrap}>
        <label className={s.searchLabel} htmlFor={`wl-search-${link.id}`}>
          Add a person or a role
        </label>
        <input
          id={`wl-search-${link.id}`}
          className={s.searchInput}
          type="text"
          placeholder="Start typing a name, or paste a Discord id"
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {SNOWFLAKE_RE.test(query.trim()) ? (
          <div className={s.results}>
            <button type="button" className={s.resultRow} onClick={() => void add("user", query.trim())}>
              <User size={13} /> Add as user id
            </button>
            <button type="button" className={s.resultRow} onClick={() => void add("role", query.trim())}>
              <AtSign size={13} /> Add as role id
            </button>
          </div>
        ) : results && (results.members.length || results.roles.length) ? (
          <div className={s.results}>
            {results.members.map((m) => (
              <button key={m.id} type="button" className={s.resultRow} onClick={() => void add("user", m.id)}>
                {m.avatarUrl ? <img src={m.avatarUrl} alt="" className={s.wlAvatar} /> : <User size={13} />}
                {m.name}
                <span className={s.wlKind}>user</span>
                <Plus size={12} className={s.resultAdd} />
              </button>
            ))}
            {results.roles.map((r) => (
              <button key={r.id} type="button" className={s.resultRow} onClick={() => void add("role", r.id)}>
                <span className={s.roleDot} style={r.color ? { background: r.color } : undefined} />
                {r.name}
                <span className={s.wlKind}>role</span>
                <Plus size={12} className={s.resultAdd} />
              </button>
            ))}
          </div>
        ) : searching ? (
          <div className={s.results}>
            <span className={s.resultsEmpty}>Searching...</span>
          </div>
        ) : results ? (
          <div className={s.results}>
            <span className={s.resultsEmpty}>Nobody in this server matches that.</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
